type Candle = {
  high: number;
  low: number;
  open: number;
  close: number;
};

type RiskZoneChartProps = {
  candles: Candle[];
  entry: number;
  stopLoss: number;
  takeProfit: number;
  ariaLabel: string;
  heightClass?: string;
  compact?: boolean;
};

/** Maps a 0-100 price value to a vertical percentage inside the plot area. */
function y(value: number) {
  return 8 + (1 - value / 100) * 84;
}

export default function RiskZoneChart({
  candles,
  entry,
  stopLoss,
  takeProfit,
  ariaLabel,
  heightClass = 'h-[240px] sm:h-[280px]',
  compact = false,
}: RiskZoneChartProps) {
  const entryY = y(entry);
  const stopY = y(stopLoss);
  const targetY = y(takeProfit);

  const labelClass = compact
    ? 'absolute right-0 -translate-y-1/2 rounded-sm px-1.5 py-0.5 text-[9px] font-semibold leading-none'
    : 'absolute right-0 -translate-y-1/2 rounded-sm px-1.5 py-1 text-[10px] font-semibold leading-none';

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={`relative w-full overflow-hidden rounded-lg bg-background-50 ${heightClass}`}
    >
      <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />

      {[16, 38, 60, 82].map((line) => (
        <span
          key={line}
          aria-hidden="true"
          className="absolute left-0 right-20 border-t border-background-200/80"
          style={{ top: `${line}%` }}
        />
      ))}

      <span
        aria-hidden="true"
        className="absolute left-0 right-20 bg-primary-50/80"
        style={{ top: `${targetY}%`, height: `${Math.max(0, entryY - targetY)}%` }}
      />
      <span
        aria-hidden="true"
        className="absolute left-0 right-20 bg-accent-50"
        style={{ top: `${entryY}%`, height: `${Math.max(0, stopY - entryY)}%` }}
      />

      <span
        aria-hidden="true"
        className="absolute left-0 right-20 border-t-2 border-solid border-foreground-400"
        style={{ top: `${entryY}%` }}
      />
      <span className={`${labelClass} bg-background-100 text-foreground-700`} style={{ top: `${entryY}%` }}>
        จุดเข้า
      </span>

      <span
        aria-hidden="true"
        className="absolute left-0 right-20 border-t border-dashed border-primary-500"
        style={{ top: `${targetY}%` }}
      />
      <span className={`${labelClass} bg-primary-100 text-primary-800`} style={{ top: `${targetY}%` }}>
        Take Profit
      </span>

      <span
        aria-hidden="true"
        className="absolute left-0 right-20 border-t border-dashed border-accent-500"
        style={{ top: `${stopY}%` }}
      />
      <span className={`${labelClass} bg-accent-100 text-accent-900`} style={{ top: `${stopY}%` }}>
        Stop Loss
      </span>

      <div className="absolute inset-y-1 left-2 right-20 flex items-stretch gap-[3px]">
        {candles.map((candle, index) => {
          const up = candle.close >= candle.open;
          const bodyTop = Math.min(y(candle.open), y(candle.close));
          const bodyHeight = Math.max(2.2, Math.abs(y(candle.open) - y(candle.close)));
          return (
            <div key={index} className="relative h-full flex-1">
              <span
                aria-hidden="true"
                className="absolute left-1/2 w-px -translate-x-1/2 bg-foreground-400/60"
                style={{ top: `${y(candle.high)}%`, height: `${y(candle.low) - y(candle.high)}%` }}
              />
              <span
                aria-hidden="true"
                className={`absolute left-1/2 w-full max-w-[14px] -translate-x-1/2 rounded-[1px] ${
                  up ? 'bg-primary-500' : 'bg-accent-500'
                }`}
                style={{ top: `${bodyTop}%`, height: `${bodyHeight}%` }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}